import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from '@/hooks/errors/use-toast';

interface UploadDropzoneProps {
  onFileSelected: (file: File) => void;
  allowedTypes: string[];
  maxSizeMB: number;
  triggerFileInput?: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  autoUpload?: boolean;
}

export interface UploadDropzoneRef {
  handlePasteEvent: (event: ClipboardEvent) => void;
  focus: () => void;
}

const UploadDropzone = forwardRef<UploadDropzoneRef, UploadDropzoneProps>(({
  onFileSelected,
  allowedTypes,
  maxSizeMB,
  triggerFileInput,
  fileInputRef,
  autoUpload = false
}, ref) => {
  const [dragActive, setDragActive] = useState(false);
  const [pasteActive, setPasteActive] = useState(false);
  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  
  // Use external ref if provided, otherwise use internal ref
  const inputRef = fileInputRef || internalFileInputRef;

  // Handle file selection
  const handleFileChange = (file: File | null) => {
    if (!file) return;
    
    // File validation
    if (!allowedTypes.includes(file.type)) {
      const allowedExtensions = allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ');
      toast({
        title: "Invalid file type",
        description: `Please upload ${allowedExtensions} files only.`,
        variant: "error",
      });
      return { error: `Invalid file type. Please upload ${allowedExtensions}.` };
    }
    
    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > maxSizeMB) {
      const maxSizeKB = Math.round(maxSizeMB * 1024);
      toast({
        title: "File too large",
        description: `Maximum size allowed is ${maxSizeKB}KB. Please use a file smaller than ${maxSizeKB}KB. Your file is ${sizeInMB.toFixed(2)}MB.`,
        variant: "error",
      });
      return { error: `File size exceeds ${maxSizeMB}MB limit.` };
    }
    
    // Pass the file to the parent component
    onFileSelected(file);
    return { error: null };
  };

  // Handle paste events
  const handlePaste = async (e: ClipboardEvent) => {
    e.preventDefault();
    
    if (!e.clipboardData?.items) return;

    // Show paste feedback
    setPasteActive(true);
    setTimeout(() => setPasteActive(false), 200);

    // Look for image files in clipboard
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          console.log('[UploadDropzone] Image pasted from clipboard:', {
            name: file.name || 'pasted-image',
            type: file.type,
            size: file.size
          });
          
          // Create a proper file with a name if it doesn't have one
          const namedFile = file.name ? file : new File([file], `pasted-image-${Date.now()}.${file.type.split('/')[1]}`, { type: file.type });
          
          handleFileChange(namedFile);
          return; // Only process the first image found
        }
      }
    }

    // If no image found, show helpful message
    toast({
      title: "No image found",
      description: "Please copy an image to your clipboard first, then paste here.",
      variant: "error",
    });
  };

  // Expose methods to parent components via ref
  useImperativeHandle(ref, () => ({
    handlePasteEvent: handlePaste,
    focus: () => {
      dropzoneRef.current?.focus();
    }
  }), [allowedTypes, maxSizeMB, onFileSelected]);

  // Add paste event listener
  useEffect(() => {
    const dropzone = dropzoneRef.current;
    if (!dropzone) return;

    // Make dropzone focusable for paste events
    dropzone.setAttribute('tabindex', '0');

    const pasteHandler = (e: ClipboardEvent) => handlePaste(e);
    
    // Listen for paste events on the dropzone
    dropzone.addEventListener('paste', pasteHandler);
    
    // Also listen for global paste events when dropzone is focused
    const globalPasteHandler = (e: ClipboardEvent) => {
      if (document.activeElement === dropzone || dropzone.contains(document.activeElement)) {
        handlePaste(e);
      }
    };
    
    document.addEventListener('paste', globalPasteHandler);

    return () => {
      dropzone.removeEventListener('paste', pasteHandler);
      document.removeEventListener('paste', globalPasteHandler);
    };
  }, [allowedTypes, maxSizeMB, onFileSelected]);

  // Handle drag events
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileChange(e.target.files[0]);
    }
  };

  // Handle click on dropzone - Improved to ensure it always works correctly
  const handleDropzoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Ensure consistent behavior for click handling
    if (triggerFileInput) {
      triggerFileInput();
    } else if (inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={allowedTypes.join(',')}
        className="hidden"
        onChange={handleInputChange}
        aria-label="Upload image file"
      />
      
      <div
        ref={dropzoneRef}
        className={`flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed transition-all min-h-[240px] w-full cursor-pointer outline-none focus:ring-2 focus:ring-primary/20
          ${dragActive 
            ? 'border-primary bg-primary/5' 
            : pasteActive
            ? 'border-green-500 bg-green-500/5'
            : 'border-accent/20 hover:border-accent/40 bg-accent/5'}`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={handleDropzoneClick}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-center gap-3"
        >
          <div className="p-4 bg-accent/10 rounded-full">
            <ImageIcon size={36} className="text-accent/80" />
          </div>
          <p className="text-lg font-medium text-foreground">
            {dragActive ? 'Drop your image here' : pasteActive ? 'Image pasted!' : 'Drag and drop, click to browse, or paste image'}
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Accepted formats: {allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}
            <br />Max size: {Math.round(maxSizeMB * 1024)}KB
          </p>
        </motion.div>
      </div>
    </>
  );
});

UploadDropzone.displayName = 'UploadDropzone';

export default UploadDropzone;
